import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useGamesState } from '../../../api/games/useGames';
import { GameStatisticsPlayerResult, StatisticUnit, useGameStatistics } from './useGameStatistics';
import { Box, Grid, Typography } from '@material-ui/core';
import { Option } from '../../../components/inputs/singleSelect/option';
import { MaterialSelect } from '../../../components/inputs/singleSelect/MaterialSelectField';
import { toErrorMessage } from '../../../api/error/error';
import { DataContainer } from '../../../components/layout/DataContainer';
import { StatisticsDateRangeSelection } from './StatisticsDateRangeSelection';
import { EmptyContainer } from '../../../components/empty/EmptyContainer';
import { useTheme } from '@material-ui/core/styles';

const options: Option[] = [
  { value: 'gamesPlayed', label: 'Spiele', id: 'gamesPlayed' },
  { value: 'gamesWon', label: 'Siege', id: 'gamesWon' },
  { value: 'winPercentage', label: 'Siegquote', id: 'winPercentage' },
  {
    value: 'numberOfChallenges',
    label: 'Forderungen',
    id: 'numberOfChallenges',
  },
  {
    value: 'numberOfDefenses',
    label: 'Gefordert worden',
    id: 'numberOfDefenses',
  },
];

type StatKey = keyof Omit<GameStatisticsPlayerResult, 'player'>;

export const CupStatistics = (): ReactElement => {
  const theme = useTheme();
  const { finishedGames, isLoading, isError, isSuccess, error } = useGamesState();

  const initialToDate = new Date();
  const [fromDate, setFromDate] = useState(new Date(new Date().setFullYear(initialToDate.getFullYear() - 1)));
  const [toDate, setToDate] = useState(initialToDate);

  useEffect(() => {
    if (!finishedGames || finishedGames.length === 0) {
      return;
    }
    const newFromDate = finishedGames.reduce((earliest, current) => {
      return current.gameResultTimestamp < earliest.gameResultTimestamp ? current : earliest;
    }).gameResultTimestamp;
    setFromDate(newFromDate);
  }, [finishedGames]);

  const statistics = useGameStatistics(finishedGames ?? [], fromDate, toDate);

  const [selectedKey, setSelectedKey] = useState<StatKey>('gamesPlayed');
  const selectedStatistic = useMemo(() => {
    return sortByStatKey(statistics.playerStatistics, selectedKey);
  }, [statistics.playerStatistics, selectedKey]);

  const determineEmptyMessage = () => {
    if (isLoading) return 'Statistiken werden geladen';
    if (isError) return `Fehler beim Laden.\n${toErrorMessage(error)}`;
    return 'Keine Statistiken vorhanden.';
  };

  return (
    <DataContainer
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={!finishedGames || finishedGames.length === 0}
      emptyMessage={determineEmptyMessage()}
    >
      <Box display="flex" flexDirection="column" width="100%" style={{ gap: '16px' }}>
        <div>
          <Grid container>
            <Grid item xs={12} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <MaterialSelect
                options={options}
                value={selectedKey}
                label={'Auswahl'}
                onChange={(optionValue) => {
                  setSelectedKey(optionValue);
                }}
                dense
                name={'select'}
                style={{ width: '100%' }}
                noHelperTextSpace
              />
              <StatisticsDateRangeSelection
                defaultFrom={fromDate}
                defaultTo={initialToDate}
                from={fromDate}
                to={toDate}
                onChange={(from, to) => {
                  setFromDate(from);
                  setToDate(to);
                }}
              />
            </Grid>
          </Grid>
          <div style={{ padding: '8px 0' }}>
            <hr style={{ color: theme.palette.primary.light }} />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {(!selectedStatistic || selectedStatistic.length === 0) && (
              <EmptyContainer emptyMessage="Keine Statistik für diesen Zeitraum gefunden" />
            )}
            {selectedStatistic.map((v) => {
              return (
                <div key={v.player.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography component="div">
                    {v[selectedKey].rank}. {v.player.name}
                  </Typography>
                  <Typography component="div">
                    {formatStatisticValue(v[selectedKey].value, v[selectedKey].unit)}
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
        <div
          style={{
            visibility: selectedKey === 'gamesPlayed' ? 'visible' : 'hidden',
            textAlign: 'right',
          }}
        >
          <Typography component="div">
            Spiele gesamt:{' '}
            <Typography component="span" style={{ fontWeight: 'bold' }}>
              {statistics.totalGamesPlayed}
            </Typography>
          </Typography>
        </div>
      </Box>
    </DataContainer>
  );
};

const integerFormatter = new Intl.NumberFormat('de', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat('de', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});
const percentFormatter = new Intl.NumberFormat('de', {
  style: 'percent',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});
const formatStatisticValue = (value: number, unit: StatisticUnit): string => {
  if (isNaN(value)) {
    return '';
  }
  if (unit === 'integer') {
    return integerFormatter.format(value);
  }
  if (unit === 'number') {
    return numberFormatter.format(value);
  }
  if (unit === 'percentage') {
    return percentFormatter.format(value);
  }
};

const sortByStatKey = (playerStatistics: GameStatisticsPlayerResult[], statKey: StatKey): any => {
  return [...playerStatistics]
    .sort((a, b) => b[statKey].value - a[statKey].value)
    .map((p) => {
      return {
        player: p.player,
        [statKey]: p[statKey],
      };
    });
};
